/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import MDDatePicker from 'components/MDDatePicker';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import SearchProduct from 'layouts/receive/SearchProduct';
import SearchRequestList from './SearchRequestList';
import SearchReceive from './SearchReceive';
import ProductChange from './ProductChange';

const initAttr = {
  id: '',
  list_id: '',
  return_id: '',
  product_id: '',
  sku: '',
  code: '',
  image: '',
  name: '',
  location_id: '',
  location_name: '',
  unit_name: '',
  barcode: '',
  product_name: '',
  price: '',
  cost: '',
  quantity: '',
  quantity_receive: '',
  quantity_free: '',
  amount: '',
  unit_id: '',
  warehouse_id: '',
  lot: '',
  discount_value: '',
  discount_type: 'B',
  is_expire: 'N',
  date_expire: Func.dateNow(),
  created_at: '',
  updated_at: '',
};

const initParent = {
  id: '',
  code: '',
  bill_no: '',
  branch_id: '',
  supplier_id: '',
  user_id: '',
  request_id: '0',
  poauto_id: '0',
  location_id: '',
  image_id: '',
  date_shipping: '0000-00-00',
  date_due: '0000-00-00',
  deposit: '0.00',
  cn_total: '0.00',
  vat_id: '2',
  discount: '0.00',
  discount_value: '0.00',
  discount_type: 'B',
  discount_unit: 'Y',
  total: '',
  shipping: '',
  amount: '',
  vat: '',
  vat_value: '',
  amount_vat: '',
  nettotal: '',
  paid: '',
  balance: '',
  remark: '',
  kind: 'WEB',
  add_stock: 'N',
  print_no: '0',
  checkall: 'N',
  status: 'edit',
  created_at: '',
  updated_at: '',
  request_pr: 'N',
  excel: '',
  print: '',
  date_record: Func.dateNow(),
};

const initPhoto = {
  id: '',
  code: '',
  sku: '',
  name: '',
  image: '',
};

const ReturnSupplierList = forwardRef(({ openPhoto }, ref) => {
  const mod = 'return_supplier_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [attr, setAttr] = useState(initAttr);
  const [changeAttr, setChangeAttr] = useState(initAttr);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [changeConfirm, setChangeConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalChange, setModalChange] = useState(false);
  const [parent, setParent] = useState(initParent);
  const [discountChange, setDiscountChange] = useState('');
  const [shippingChange, setShippingChange] = useState('');
  const [modalPhoto, setModalPhoto] = useState(false);
  const [photo, setPhoto] = useState(initPhoto);
  const [depositChange, setDepositChange] = useState('');
  const txtQuantity = useRef(null);
  const txtChangeQuantity = useRef(null);
  const tableRef = useRef(null);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const scrollLeftRight = () => {
    if (tableRef.current.scrollLeft > 0) {
      tableRef.current.scrollLeft = 0;
    } else {
      tableRef.current.scrollLeft += 1000;
    }
  };

  const view = () => {
    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
      }
    });
  };

  const open = (item) => {
    setAttr(item);
    setModalEdit(true);
    setConfirm(false);
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.barcode_id, 1)) return;

    setConfirm(true);
  };

  const add = (item) => {
    setConfirm(false);
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      return_id: id,
      list_id: item.list_id,
      barcode_id: item.barcode_id,
      quantity: item.quantity,
      quantity_receive: item.quantity,
      lot: item.lot,
      is_expire: item.is_expire,
      date_expire: item.date_expire,
      price: item.price,
      cost: item.cost_fix,
      discount_value: 0,
      discount_type: 'B',
      unit_id: item.unit_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.quantity_receive, 1)) return;

    setConfirm(true);
  };

  const edit = () => {
    setConfirm(false);
    setLoading(true);
    setModalEdit(false);

    FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
      quantity_receive: attr.quantity_receive,
      quantity_free: attr.quantity_free,
      lot: attr.lot,
      is_expire: attr.is_expire,
      date_expire: attr.date_expire,
      price: attr.price,
      cost: attr.cost,
      discount_type: attr.discount_type,
      discount_value: attr.discount_value,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        setModalEdit(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editReceive = (item) => {
    setConfirm(false);
    setLoading(true);

    FetchData('PATCH', `/${mod}/${item.parent_id}`, app.token, {
      quantity: item.quantity,
      quantity_receive: item.quantity_receive,
      quantity_free: item.quantity_free,
      lot: item.lot,
      is_expire: item.is_expire,
      date_expire: item.date_expire,
      price: item.price,
      cost: item.cost,
      unit_id: item.unit_id,
      discount_type: item.discount_type,
      receivelist_id: item.receivelist_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editDiscount = (item) => {
    FetchData('OPTIONS', `/return_supplier/discount`, app.token, {
      ref_id: id,
      discount_value: item.discount_value,
      discount_type: item.discount_type,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const changeFormConfirm = () => {
    if (!Func.verifyLength(changeAttr.quantity, 1)) return;

    setChangeConfirm(true);
  };

  const change = () => {
    setChangeConfirm(false);
    setLoading(true);
    setModalChange(false);

    FetchData('OPTIONS', `/${mod}/change`, app.token, {
      ref_id: changeAttr.ref_id,
      barcodechange_id: changeAttr.id,
      quantity_change: changeAttr.quantity,
      lot: changeAttr.lot,
      is_expire: changeAttr.is_expire,
      date_expire: changeAttr.date_expire,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        setModalChange(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const changeInit = (item) => {
    setChangeConfirm(false);
    setChangeAttr({
      ...item,
      lot: '',
      is_expire: 'N',
      date_expire: Func.dateNow(),
    });
    setModalChange(true);

    setTimeout(function () {
      txtChangeQuantity.current.focus();
    }, 500);
  };

  const sendType = (type) => {
    setParent({ ...parent, discount_type: type });
    editDiscount({ discount_value: parent.discount_value, discount_type: type });
  };

  const sendValue = () => {
    if (parent.discount_value * 1 !== discountChange * 1) {
      setDiscountChange(parent.discount_value);
      editDiscount({ discount_value: parent.discount_value, discount_type: parent.discount_type });
    }
  };

  const sendShipping = () => {
    if (parent.shipping * 1 === shippingChange * 1) return;

    setShippingChange(parent.shipping);

    FetchData('OPTIONS', `/return_supplier/shipping`, app.token, {
      ref_id: id,
      shipping: parent.shipping,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const sendDeposit = () => {
    if (parent.cn_total * 1 === depositChange * 1) return;

    setDepositChange(parent.cn_total);

    FetchData('OPTIONS', `/${mod}/deposit`, app.token, {
      ref_id: id,
      cn_total: parent.cn_total,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editDiscountUnit = (input) => {
    FetchData('OPTIONS', `/${mod}/editdiscount`, app.token, {
      id: parent.id,
      discount_unit: input,
    }).then((result) => {
      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.${result.msg}`), 'error');
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const expireDisplay = (item) => {
    if (item.is_expire === 'N') return '-';

    return Func.dateDisplay(item.date_expire);
  };

  const btnEdit = (item) => {
    if (parent.status === 'success' || parent.status === 'cancel') return null;

    return (
      <Tooltip title={t(`element.edit`)}>
        <MDButton variant='text' color='success' size='small' iconOnly onClick={() => open(item)}>
          <Icon>border_color</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnDel = (item) => {
    if (parent.status === 'success' || parent.status === 'receive' || parent.status === 'cancel')
      return null;

    return (
      <Tooltip title={t(`element.delete`)}>
        <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
          <Icon>delete</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const txtDiscount = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendValue();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, discount_value: e.target.value })}
          onFocus={(e) => {
            e.target.select();
          }}
          onBlur={() => {
            sendValue();
          }}
          value={parent.discount_value}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem', pr: 2 },
            },
            endAdornment:
              parent.discount_type === 'P' ? (
                <IconButton onClick={() => sendType('B')} color='info' sx={{ p: 0 }}>
                  <Icon>percent</Icon>
                </IconButton>
              ) : (
                <IconButton onClick={() => sendType('P')} color='primary' sx={{ p: 0 }}>
                  <Icon>currency_bitcoin</Icon>
                </IconButton>
              ),
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const txtShipping = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendShipping();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, shipping: e.target.value })}
          onFocus={(e) => {
            e.target.select();
          }}
          onBlur={() => {
            sendShipping();
          }}
          value={parent.shipping}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const txtDeposit = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendDeposit();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, cn_total: e.target.value })}
          onFocus={(e) => {
            e.target.select();
          }}
          onBlur={() => {
            sendDeposit();
          }}
          value={parent.cn_total}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const renderHeader = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center' width={10}>
            No.
          </TableCell>
          <TableCell align='center' width={40}>
            รูป
          </TableCell>
          <TableCell>สินค้า</TableCell>
          <TableCell>LOT</TableCell>
          <TableCell className='nowrap'>สินค้าที่เปลี่ยน</TableCell>
          <TableCell>LOT เปลี่ยน</TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            จำนวนเปลี่ยน
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            คลังคงเหลือ
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            ส่งคืน
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            แถม
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            ราคาทุน
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            {parent.discount_unit === 'Y' ? 'ลด/หน่วย' : 'ลดรวม'}
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            ก่อนลด
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            ส่วนลด
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            รวม
          </TableCell>
          {/* <TableCell align="right" width={80} className="nowrap">
            ปันทุน
          </TableCell> */}
          <TableCell width={10}>&nbsp;</TableCell>
          <TableCell width={10}>&nbsp;</TableCell>
          <TableCell width={10}>&nbsp;</TableCell>
          <TableCell width={10}>&nbsp;</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const productChange = (row) => {
    if (row.change_code === '') return null;

    return (
      <MDBox>
        <MDBox>{row.change_code}</MDBox>
        <MDBox className='cut' width={200}>
          {row.change_name}
        </MDBox>
      </MDBox>
    );
  };

  const quantityChange = (row) => {
    if (row.change_code === '') return null;

    return (
      <MDBox align='right'>
        <MDBox>{Func.numberDisplay(row.quantity_change)}</MDBox>
        <MDBox>{row.change_unit}</MDBox>
      </MDBox>
    );
  };

  const expireDisplayChange = (row) => {
    if (row.is_expire_change === 'N') return '-';

    return Func.dateDisplay(row.date_expire_change);
  };

  const renderRecord = () => {
    return (
      <TableBody>
        {record.map((row) => (
          <TableRow key={row.id} hover>
            <TableCell align='center'>{row.no}</TableCell>
            <TableCell
              onClick={() => {
                setPhoto(row);
                setModalPhoto(true);
              }}
            >
              {Func.imageDisplay(row)}
            </TableCell>
            <TableCell>
              <MDBox className='nowrap'>
                {row.code} <span className='color-default'>{row.sku}</span>
              </MDBox>
              <MDBox className='cut' width={200}>
                {row.name}
              </MDBox>
            </TableCell>
            <TableCell>
              <MDBox className='nowrap text-center'>{row.lot}</MDBox>
              <MDBox className='nowrap text-center'>{expireDisplay(row)}</MDBox>
            </TableCell>
            <TableCell>{productChange(row)}</TableCell>
            <TableCell>
              <MDBox className='nowrap text-center'>{row.lot_change}</MDBox>
              <MDBox className='nowrap text-center'>{expireDisplayChange(row)}</MDBox>
            </TableCell>
            <TableCell>{quantityChange(row)}</TableCell>
            <TableCell align='right'>
              <MDBox>{Func.numberDisplay(row.quantity_balance)}</MDBox>
              <MDBox>{row.unit_name}</MDBox>
            </TableCell>
            <TableCell align='right'>
              <MDBox>{Func.numberDisplay(row.quantity_receive)}</MDBox>
              <MDBox>{row.unit_name}</MDBox>
            </TableCell>
            <TableCell align='right'>
              <MDBox>{Func.numberDisplay(row.quantity_free)}</MDBox>
              <MDBox>{row.unit_name}</MDBox>
            </TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.cost, 2)}</TableCell>
            <TableCell align='right' className='nowrap'>
              {row.discount_value > 0 ? (
                <span>
                  {Func.numberDisplay(row.discount_value)} {row.discount_type === 'P' ? '%' : '฿'}
                </span>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.total, 2)}</TableCell>
            <TableCell align='right'>
              {row.discount > 0 ? Func.numberDisplay(row.discount, 2) : '-'}
            </TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.amount, 2)}</TableCell>
            {/* <TableCell align="right">{Func.numberDisplay(row.fixed_cost)}</TableCell> */}
            <TableCell align='center'>
              {parent.status !== 'edit' && parent.status !== 'waiting' ? null : (
                <ProductChange parent={row} callback={changeInit} />
              )}
            </TableCell>
            <TableCell align='center'>
              <SearchReceive parent={row} callback={editReceive} />
            </TableCell>
            <TableCell align='center'>{btnEdit(row)}</TableCell>
            <TableCell align='center'>{btnDel(row)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const renderSummaryEdit = () => {
    return (
      <Table aria-label='summary table'>
        <TableBody>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าสินค้า</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.total)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>หักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right' colSpan={2}>
              {txtDiscount()}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าสินค้าหลังหักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.amount, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>
                ภาษีมูลค่าเพิ่ม {Func.numberDisplay(parent.vat_value, 0)}%
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.vat, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่ารวมภาษี</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5' color='primary'>
                {Func.numberDisplay(parent.amount_vat, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>ค่าจัดส่ง</MDTypography>
            </TableCell>
            <TableCell align='right'>{txtShipping()}</TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>รวมทั้งสิ้น</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5' color='info'>
                {Func.numberDisplay(parent.nettotal, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าใบลดหนี้</MDTypography>
            </TableCell>
            <TableCell align='right'>{txtDeposit()}</TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>คงเหลือ</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5' color='error'>
                {Func.numberDisplay(parent.balance, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  const renderSummary = () => {
    return (
      <Table aria-label='summary table'>
        <TableBody>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าสินค้า</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.total, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>หักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>
                {Func.numberDisplay(parent.discount_value, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>{parent.discount_type === 'P' ? '%' : 'บาท'}</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าสินค้าหลังหักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.amount, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>
                ภาษีมูลค่าเพิ่ม {Func.numberDisplay(parent.vat_value, 0)}%
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.vat, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่ารวมภาษี</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5' color='primary'>
                {Func.numberDisplay(parent.amount_vat, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>ค่าจัดส่ง</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.shipping, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>รวมทั้งสิ้น</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5' color='info'>
                {Func.numberDisplay(parent.nettotal, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  const btnDiscountUnit = () => {
    if (parent.status === 'success' || parent.status === 'cancel') return null;

    if (parent.discount_unit === 'Y') {
      return (
        <MDButton variant='outlined' color='primary' onClick={() => editDiscountUnit('N')}>
          <Icon>local_offer</Icon> ลด / หน่วย
        </MDButton>
      );
    }

    return (
      <MDButton variant='outlined' color='warning' onClick={() => editDiscountUnit('Y')}>
        <Icon>discount</Icon> ลดรวม
      </MDButton>
    );
  };

  const renderButton = () => {
    if (parent.status === 'success' || parent.status === 'receive' || parent.status === 'cancel')
      return null;

    return (
      <MDBox display='flex' justifyContent='flex-end' gap={1} p={2}>
        {btnDiscountUnit()}
        {parent.request_id * 1 > 0 ? <SearchRequestList add={add} /> : null}
        <SearchProduct add={add} />
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MDBox p={2} pt={3}>
              <MDTypography variant='h5'>{t(`element.order`)}</MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={6}>
            {renderButton()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer
              sx={{ boxShadow: 0, minHeight: 300 }}
              ref={tableRef}
              onDoubleClick={scrollLeftRight}
            >
              <Table aria-label='simple table'>
                {renderHeader()}
                {renderRecord()}
              </Table>
            </TableContainer>
            <TableContainer sx={{ boxShadow: 0, mt: 2 }}>
              {parent.status === 'success' || parent.status === 'cancel'
                ? renderSummary()
                : renderSummaryEdit()}
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={5}>
        <Grid item xs={12} md={12} lg={6}>
          <Paper sx={{ p: 2 }} variant='outlined'>
            {attr.image === '' ? null : (
              <MDBox component='img' src={attr.image} alt='' width='100%' height='100%' />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDInput
                label='รหัสสินค้า'
                value={attr.code}
                variant='standard'
                fullWidth
                inputProps={{
                  autoComplete: 'off',
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='สินค้า'
                value={attr.name}
                variant='standard'
                fullWidth
                multiline
                inputProps={{
                  autoComplete: 'off',
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label='ส่งคืน'
                onChange={(e) => setAttr({ ...attr, quantity_receive: e.target.value })}
                onFocus={(e) => e.target.select()}
                value={attr.quantity_receive}
                variant='standard'
                type='number'
                fullWidth
                InputProps={{
                  inputRef: txtQuantity,
                  autoComplete: 'off',
                  endAdornment: (
                    <InputAdornment position='start'>
                      <span>{attr.unit_name}</span>
                    </InputAdornment>
                  ),
                  inputProps: {
                    autoComplete: 'off',
                    className: 'text-right',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label='แถม'
                onChange={(e) => setAttr({ ...attr, quantity_free: e.target.value })}
                onFocus={(e) => e.target.select()}
                value={attr.quantity_free}
                variant='standard'
                type='number'
                fullWidth
                InputProps={{
                  autoComplete: 'off',
                  endAdornment: (
                    <InputAdornment position='start'>
                      <span>{attr.unit_name}</span>
                    </InputAdornment>
                  ),
                  inputProps: {
                    autoComplete: 'off',
                    className: 'text-right',
                  },
                }}
              />
            </Grid>
            {/* <Grid item xs={12} sm={6}>
              <MDInput
                label="ราคา"
                onChange={(e) => setAttr({ ...attr, price: e.target.value })}
                onFocus={(e) => e.target.select()}
                value={attr.price}
                variant="standard"
                type="number"
                fullWidth
                InputProps={{
                  autoComplete: "off",
                  endAdornment: (
                    <InputAdornment position="start">
                      <span>บาท</span>
                    </InputAdornment>
                  ),
                  inputProps: {
                    autoComplete: "off",
                    className: "text-right",
                  },
                }}
              />
            </Grid> */}
            <Grid item xs={12} sm={6}>
              <MDInput
                label='ราคาทุน'
                onChange={(e) => setAttr({ ...attr, cost: e.target.value })}
                onFocus={(e) => e.target.select()}
                value={attr.cost}
                variant='standard'
                type='number'
                fullWidth
                InputProps={{
                  autoComplete: 'off',
                  endAdornment: (
                    <InputAdornment position='start'>
                      <span>บาท</span>
                    </InputAdornment>
                  ),
                  inputProps: {
                    autoComplete: 'off',
                    className: 'text-right',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label='ส่วนลด'
                onChange={(e) => setAttr({ ...attr, discount_value: e.target.value })}
                onFocus={(e) => e.target.select()}
                value={attr.discount_value}
                variant='standard'
                type='number'
                fullWidth
                InputProps={{
                  inputProps: {
                    maxLength: 20,
                    autoComplete: 'off',
                    className: 'text-right',
                    sx: { minWidth: 80 },
                  },
                  endAdornment:
                    attr.discount_type === 'P' ? (
                      <IconButton
                        onClick={() => setAttr({ ...attr, discount_type: 'B' })}
                        color='info'
                        sx={{ p: 0 }}
                      >
                        <Icon>percent</Icon>
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => setAttr({ ...attr, discount_type: 'P' })}
                        color='primary'
                        sx={{ p: 0 }}
                      >
                        <Icon>currency_bitcoin</Icon>
                      </IconButton>
                    ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='LOT'
                onChange={(e) => setAttr({ ...attr, lot: e.target.value })}
                value={attr.lot}
                variant='standard'
                fullWidth
                InputProps={{
                  autoComplete: 'off',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={attr.is_expire === 'Y'}
                    onChange={(e) => setAttr({ ...attr, is_expire: e.target.checked ? 'Y' : 'N' })}
                    inputProps={{
                      'aria-label': 'enable',
                    }}
                  />
                }
                label='เพิ่มวันหมดอายุ'
              />
            </Grid>
            {attr.is_expire === 'Y' ? (
              <Grid item xs={12} sm={6}>
                <MDDatePicker
                  onChange={([date]) => {
                    setAttr({ ...attr, date_expire: Func.dateFormat(date) });
                  }}
                  value={new Date(attr.date_expire)}
                  input={{
                    label: 'วันหมดอายุ',
                    variant: 'standard',
                    fullWidth: true,
                  }}
                />
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='md' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)} : {attr.barcode}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog open={modalEdit} maxWidth='md' keepMounted aria-describedby='dialog-edit' fullWidth>
        <DialogTitle>
          {t(`element.edit`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalEdit(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='success' onClick={editConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderChangeInfo = () => {
    return (
      <Grid container spacing={5}>
        <Grid item xs={12} md={12} lg={6}>
          <Paper sx={{ p: 2 }} variant='outlined'>
            {changeAttr.image === '' ? null : (
              <MDBox component='img' src={changeAttr.image} alt='' width='100%' height='100%' />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDInput
                label='รหัสสินค้า'
                value={changeAttr.code}
                variant='standard'
                fullWidth
                inputProps={{
                  autoComplete: 'off',
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='สินค้า'
                value={changeAttr.name}
                variant='standard'
                fullWidth
                multiline
                inputProps={{
                  autoComplete: 'off',
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label='จำนวนเปลี่ยน'
                onChange={(e) => setChangeAttr({ ...changeAttr, quantity: e.target.value })}
                onFocus={(e) => e.target.select()}
                value={changeAttr.quantity}
                variant='standard'
                type='number'
                fullWidth
                InputProps={{
                  inputRef: txtChangeQuantity,
                  autoComplete: 'off',
                  endAdornment: (
                    <InputAdornment position='start'>
                      <span>{changeAttr.unit_name}</span>
                    </InputAdornment>
                  ),
                  inputProps: {
                    autoComplete: 'off',
                    className: 'text-right',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='LOT'
                onChange={(e) => setChangeAttr({ ...changeAttr, lot: e.target.value })}
                value={changeAttr.lot}
                variant='standard'
                fullWidth
                InputProps={{
                  autoComplete: 'off',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={changeAttr.is_expire === 'Y'}
                    onChange={(e) =>
                      setChangeAttr({ ...changeAttr, is_expire: e.target.checked ? 'Y' : 'N' })
                    }
                    inputProps={{
                      'aria-label': 'enable',
                    }}
                  />
                }
                label='เพิ่มวันหมดอายุ'
              />
            </Grid>
            {changeAttr.is_expire === 'Y' ? (
              <Grid item xs={12} sm={6}>
                <MDDatePicker
                  onChange={([date]) => {
                    setChangeAttr({ ...changeAttr, date_expire: Func.dateFormat(date) });
                  }}
                  value={new Date(changeAttr.date_expire)}
                  input={{
                    label: 'วันหมดอายุ',
                    variant: 'standard',
                    fullWidth: true,
                  }}
                />
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderChange = () => {
    return (
      <Dialog
        open={modalChange}
        maxWidth='md'
        keepMounted
        aria-describedby='dialog-change'
        fullWidth
      >
        <DialogTitle>
          แลกเปลี่ยนสินค้า : {changeAttr.code}
          <IconButton
            aria-label='close'
            onClick={() => setModalChange(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderChangeInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {changeConfirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setChangeConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={change}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={changeFormConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.code} <span className='color-default'>{photo.sku}</span>
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={1}>
            <MDTypography variant='body'>{photo.name}</MDTypography>
          </MDBox>
          <MDBox
            component='img'
            src={photo.image}
            alt=''
            borderRadius='lg'
            width='100%'
            position='relative'
            sx={{ boxShadow: 0 }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  useImperativeHandle(ref, () => {
    return {
      view,
    };
  });

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view();
  }, [id]);

  return (
    <div>
      {renderList()}
      {renderAdd()}
      {renderEdit()}
      {renderChange()}
      {renderPhoto()}
      {renderLoading()}
      {notify}
    </div>
  );
});

ReturnSupplierList.propTypes = {
  openPhoto: PropTypes.func.isRequired,
};

export default ReturnSupplierList;
